import { IS_MUTINY } from '../constants';
import { fetchWithTimeout } from './fetch-with-timeout';
import { getCookie } from './get-cookie';

const CLOCK_SKEW_SECONDS = 5;

/**
 * Reads the `exp` (expiry) claim from a JWT without verifying its signature.
 * Returns the expiry as a Unix timestamp in seconds, or `null` if the token is
 * absent or cannot be parsed.
 */
const getAccessTokenExpirySeconds = (token?: string): number | null => {
  const payload = token?.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const { exp } = JSON.parse(json) as { exp?: number };
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

/**
 * Decides whether an access token should be attached to an outgoing request.
 *
 * Skips absent tokens and tokens that have already expired (with a small
 * clock-skew allowance) so we never send a token the Edge API will reject with
 * IDX10223. If the token cannot be parsed we still attach it, preserving the
 * previous behaviour rather than risk dropping an otherwise valid token.
 */
export const canUseAccessToken = (token?: string): boolean => {
  if (!token) {
    return false;
  }

  const exp = getAccessTokenExpirySeconds(token);
  if (exp === null) {
    return true;
  }

  return exp > Date.now() / 1000 + CLOCK_SKEW_SECONDS;
};

export const authedFetch = async (
  tenant: string,
  url: string,
  settings: RequestInit,
  tokenOverride: string,
  headers?: HeadersInit,
  timeout?: number
): Promise<Response> => {
  const token: string = tokenOverride;

  const requestHeaders = new Headers({
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    'X-SOCKET-ID': localStorage?.getItem('WEBSOCKET:SESSIONID') || '',
    'X-SESSION-ID': getCookie('P2G.Session.Id'),
    'x-mutiny': IS_MUTINY,
    'x-tenant': tenant
  });

  // Only attach a token that is present and not expired - stops the app sending
  // dead tokens that the Edge API rejects with IDX10223.
  if (canUseAccessToken(token)) {
    requestHeaders.set('Authorization', 'Bearer ' + token);
  }

  // Caller-provided headers take precedence, matching the previous spread order.
  if (headers) {
    new Headers(headers).forEach((value, key) => requestHeaders.set(key, value));
  }

  const defaultSettings: RequestInit = {
    method: 'GET',
    headers: requestHeaders
  };

  const combinedSettings: RequestInit = { ...defaultSettings, ...settings };
  if (timeout) {
    return fetchWithTimeout(url, combinedSettings, timeout);
  }

  return fetch(url, combinedSettings);
};
export const authedFileFetch = async (
  url: string,
  settings: RequestInit,
  tokenOverride: string,
  headers?: HeadersInit,
  timeout?: number
): Promise<Response> => {
  const token: string = tokenOverride;

  const requestHeaders = new Headers({
    'X-SOCKET-ID': localStorage?.getItem('WEBSOCKET:SESSIONID') || '',
    'X-SESSION-ID': getCookie('P2G.Session.Id'),
    'x-mutiny': IS_MUTINY
  });

  if (canUseAccessToken(token)) {
    requestHeaders.set('Authorization', 'Bearer ' + token);
  }

  if (headers) {
    new Headers(headers).forEach((value, key) => requestHeaders.set(key, value));
  }

  const defaultSettings: RequestInit = {
    method: 'GET',
    headers: requestHeaders
  };

  const combinedSettings: RequestInit = { ...defaultSettings, ...settings };
  if (timeout) {
    return fetchWithTimeout(url, combinedSettings, timeout);
  }

  return fetch(url, combinedSettings);
};
